<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Cortesias</span>
          <h2 class="workspace-title">Emision rapida, links y lotes</h2>
          <p class="workspace-copy">Todo nace desde un modal launcher: emite una cortesia al instante, crea un link privado o procesa un CSV sin salir de esta seccion.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact courtesy-hero-actions">
          <button class="workspace-button workspace-button--primary" type="button" @click="openComposer">
            <i class="fas fa-wand-magic-sparkles"></i>
            <span>Crear desde modal</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Plantillas</small>
          <strong>{{ courtesyTickets.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Basadas en etapa</small>
          <strong>{{ stageBoundCourtesyCount }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Con codigo</small>
          <strong>{{ codedCourtesyCount }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activas</small>
          <strong>{{ activeCourtesyCount }}</strong>
        </article>
      </div>
    </article>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel workspace-panel--sticky-top courtesy-tabs-panel">
      <div class="courtesy-mode-grid" role="tablist" aria-label="Tipos de cortesia">
        <button
          v-for="tab in courtesyTabs"
          :key="tab.id"
          :class="['courtesy-mode-card', { 'is-active': viewMode === tab.id }]"
          type="button"
          @click="viewMode = tab.id"
        >
          <span class="courtesy-mode-card__icon"><i :class="tab.icon"></i></span>
          <div class="courtesy-mode-card__copy">
            <small>{{ tab.eyebrow }}</small>
            <strong>{{ tab.label }}</strong>
            <p>{{ tab.helper }}</p>
          </div>
          <span class="courtesy-mode-card__meta">{{ tab.meta }}</span>
        </button>
      </div>

      <div class="courtesy-tab-brief">
        <div>
          <span class="workspace-kicker">{{ activeCourtesyTab.kicker }}</span>
          <h2 class="workspace-title">{{ activeCourtesyTab.title }}</h2>
          <p class="workspace-copy">{{ activeCourtesyTab.copy }}</p>
        </div>
        <div class="courtesy-tab-brief__actions">
          <span class="workspace-badge courtesy-tab-brief__badge">{{ activeCourtesyTab.meta }}</span>
          <button class="workspace-button workspace-button--primary" type="button" @click="openComposer(activeCourtesyTab.modalMode)">
            <i :class="activeCourtesyTab.actionIcon"></i>
            <span>{{ activeCourtesyTab.actionLabel }}</span>
          </button>
        </div>
      </div>
    </article>

    <article v-if="viewMode === 'manual' && !lastIssuedBatch.length" class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Cortesia manual</span>
          <h2 class="workspace-title">Emite accesos uno a uno</h2>
          <p class="workspace-copy">Ideal para prensa, invitados VIP, artistas o staff puntual. Todo sale desde el modal sin crear pasos extra.</p>
        </div>
      </div>
    </article>

    <article v-if="viewMode === 'codes' && !courtesyTickets.length" class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Codigos de cortesia</span>
          <h2 class="workspace-title">Crea plantillas compartibles</h2>
          <p class="workspace-copy">Genera links privados, codigos y etapas de cortesia visibles en checkout desde el mismo modal.</p>
        </div>
      </div>
    </article>

    <article v-if="viewMode === 'csv'" class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">CSV</span>
          <h2 class="workspace-title">Importacion orientada a personas</h2>
          <p class="workspace-copy">Usa columnas `first_name,last_name,email,stage,notes`. Si `stage` no viene, puedes dejar una etapa por defecto desde el modal.</p>
        </div>
      </div>
    </article>

    <article v-if="viewMode === 'codes' && lastGeneratedLinks.length" class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Ultimo link</span>
          <h2 class="workspace-title">Link y QRs listos</h2>
          <p class="workspace-copy">Comparte este acceso desde la misma biblioteca o descarga el ZIP de QRs si lo necesitas.</p>
        </div>
        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button" type="button" :disabled="zipGenerating" @click="downloadLastBatchCsv">
            <i class="fas fa-file-csv"></i>
            <span>CSV resultados</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" :disabled="zipGenerating" @click="downloadQrZip">
            <i :class="zipGenerating ? 'fas fa-spinner fa-spin' : 'fas fa-qrcode'"></i>
            <span>{{ zipGenerating ? 'Generando ZIP...' : 'Descargar QR ZIP' }}</span>
          </button>
        </div>
      </div>
      <div class="courtesy-batch-preview">
        <span v-for="entry in lastGeneratedLinks.slice(0, 6)" :key="entry.link" class="workspace-badge">{{ entry.code || entry.name }}</span>
        <span v-if="lastGeneratedLinks.length > 6" class="workspace-badge">+{{ lastGeneratedLinks.length - 6 }} mas</span>
      </div>
    </article>

    <article v-if="viewMode === 'manual' && manualIssuedEntry" class="glass-surface workspace-panel courtesy-issued-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Confirmacion</span>
          <h2 class="workspace-title">Cortesia emitida</h2>
          <p class="workspace-copy">{{ manualIssuedEntry.holder_name || manualIssuedEntry.email }} ya tiene su acceso listo. Puedes descargar el ticket ahora mismo o emitir otra cortesia.</p>
        </div>
        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button" type="button" @click="openComposer('issue')">
            <i class="fas fa-plus"></i>
            <span>Emitir otra</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" :disabled="!manualIssuedEntry.ticket_url || issuedDownloadId === manualIssuedEntry.payment_id" @click="downloadIssuedTicket(manualIssuedEntry)">
            <i :class="issuedDownloadId === manualIssuedEntry.payment_id ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
            <span>{{ issuedDownloadId === manualIssuedEntry.payment_id ? 'Descargando...' : 'Descargar ticket' }}</span>
          </button>
        </div>
      </div>
      <div class="courtesy-confirmation-grid">
        <article class="courtesy-confirmation-card">
          <small>Invitado</small>
          <strong>{{ manualIssuedEntry.holder_name || 'Sin nombre' }}</strong>
          <p>{{ manualIssuedEntry.email }}</p>
        </article>
        <article class="courtesy-confirmation-card">
          <small>Etapa</small>
          <strong>{{ manualIssuedEntry.stage_name || 'Cortesia general' }}</strong>
          <p>{{ manualIssuedEntry.ticket_name }}</p>
        </article>
        <article class="courtesy-confirmation-card">
          <small>Operacion</small>
          <strong>Factura {{ manualIssuedEntry.invoice_id }}</strong>
          <p>{{ manualIssuedEntry.ticket_ready ? 'Ticket PDF generado y listo para descargar.' : 'La emision se completo.' }}</p>
        </article>
      </div>
    </article>

    <article v-if="viewMode === 'csv' && lastIssuedBatch.length" class="glass-surface workspace-panel courtesy-issued-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">{{ issuedBatchKicker }}</span>
          <h2 class="workspace-title">{{ issuedBatchTitle }}</h2>
          <p class="workspace-copy">{{ issuedBatchCopy }}</p>
        </div>
        <div class="workspace-actions workspace-actions--compact">
          <button v-if="issuedBatchReadyEntries.length" class="workspace-button workspace-button--primary" type="button" :disabled="issuedZipGenerating" @click="downloadIssuedTicketsZip">
            <i :class="issuedZipGenerating ? 'fas fa-spinner fa-spin' : 'fas fa-file-zipper'"></i>
            <span>{{ issuedZipGenerating ? 'Armando ZIP...' : `Descargar ZIP (${issuedBatchReadyEntries.length})` }}</span>
          </button>
          <button class="workspace-button" type="button" @click="downloadIssuedResultsCsv">
            <i class="fas fa-file-export"></i>
            <span>Descargar CSV</span>
          </button>
        </div>
      </div>
      <div class="courtesy-batch-preview">
        <span v-for="entry in lastIssuedBatch.slice(0, 6)" :key="`${entry.email}-${entry.status}-${entry.payment_id || entry.detail}`" :class="['workspace-badge', { 'workspace-badge--danger': entry.status === 'error' }]">
          {{ entry.email }} · {{ entry.status === 'ok' ? 'emitida' : 'error' }}
        </span>
        <span v-if="lastIssuedBatch.length > 6" class="workspace-badge">+{{ lastIssuedBatch.length - 6 }} filas</span>
      </div>
    </article>

    <article v-if="viewMode === 'codes'" class="glass-surface workspace-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Biblioteca</span>
          <h2 class="workspace-title">Plantillas de cortesia</h2>
          <p class="workspace-copy">Aqui viven las etapas de cortesia que usas para links privados, checkout visible o emisiones que quieres separar en reportes.</p>
        </div>
      </div>

      <div v-if="loading && !courtesyTickets.length" class="workspace-empty">Cargando cortesias...</div>
      <div v-else-if="!courtesyTickets.length" class="workspace-empty">Todavia no hay plantillas de cortesia creadas.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table">
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Base</th>
              <th>Cantidad</th>
              <th>Entrega</th>
              <th>Codigo</th>
              <th>Link</th>
              <th>Status</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in courtesyTickets" :key="ticket.id">
              <td>
                <div class="workspace-table-title">{{ ticket.name }}</div>
                <p class="workspace-table-subcopy">{{ ticket.description || 'Sin notas.' }}</p>
              </td>
              <td>{{ ticket.courtesy_source_ticket_name || 'General' }}</td>
              <td>{{ ticket.initial_quantity }}</td>
              <td>{{ courtesyDeliveryLabel(ticket) }}</td>
              <td>{{ primaryCourtesyCode(ticket) || 'Sin codigo' }}</td>
              <td>
                <a v-if="courtesyLink(ticket)" class="workspace-inline-link" :href="courtesyLink(ticket)" target="_blank" rel="noreferrer">Abrir</a>
                <span v-else class="workspace-table-subcopy">Sin link</span>
              </td>
              <td>
                <span :class="['workspace-status-pill', ticket.status ? 'is-active' : 'is-off']">{{ ticket.status ? 'Activa' : 'Pausada' }}</span>
              </td>
              <td>
                <div class="workspace-inline-actions">
                  <button v-if="courtesyLink(ticket)" class="workspace-chip-button" type="button" @click="copyCourtesyLink(ticket)">
                    <i class="fas fa-copy"></i>
                    <span>Copiar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="editCourtesy(ticket)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" :disabled="savingTicketId === ticket.id" @click="toggleCourtesyStatus(ticket)">
                    <i :class="ticket.status ? 'fas fa-eye-slash' : 'fas fa-rotate-left'"></i>
                    <span>{{ ticket.status ? 'Pausar' : 'Reactivar' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>

    <WorkspaceWizardModal
      :model-value="modalOpen"
      kicker="Cortesias"
      :title="modalTitle"
      :copy="modalCopy"
      size="xl"
      @close-request="closeModal"
    >
      <template v-if="modalScene === 'launcher'">
        <div class="courtesy-launcher-grid">
          <button v-for="card in courtesyLauncherCards" :key="card.mode" class="courtesy-launcher-card" type="button" @click="chooseModalMode(card.mode)">
            <span class="courtesy-launcher-card__icon"><i :class="card.icon"></i></span>
            <small>{{ card.kicker }}</small>
            <strong>{{ card.title }}</strong>
            <p>{{ card.copy }}</p>
            <span class="courtesy-launcher-card__action">{{ card.action }}</span>
          </button>
        </div>
      </template>

      <form v-else id="courtesy-modal-form" class="courtesy-modal" @submit.prevent="submitCourtesy">
        <div class="courtesy-modal-topbar">
          <button class="workspace-chip-button" type="button" @click="backToLauncher">
            <i class="fas fa-arrow-left"></i>
            <span>Volver</span>
          </button>
          <span class="workspace-badge">{{ modalMode === 'issue' ? 'Emitir' : modalMode === 'link' ? 'Link' : 'CSV' }}</span>
        </div>

        <template v-if="modalMode === 'issue'">
          <section class="courtesy-section">
            <div class="courtesy-section__head">
              <h3>Emitir una cortesia ahora</h3>
              <p>Asigna un acceso gratuito directamente a una persona. Si eliges una etapa, se resuelve como clon oculta para no mezclar reportes.</p>
            </div>

            <div class="courtesy-field-grid">
              <label class="workspace-field">
                <span>Nombre</span>
                <input v-model.trim="issueForm.first_name" class="workspace-input" type="text" placeholder="Sofia" required />
              </label>
              <label class="workspace-field">
                <span>Apellido</span>
                <input v-model.trim="issueForm.last_name" class="workspace-input" type="text" placeholder="Ramirez" required />
              </label>
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Correo</span>
                <input v-model.trim="issueForm.email" class="workspace-input" type="email" placeholder="sofia@email.com" required />
              </label>
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Etapa base opcional</span>
                <select v-model="issueForm.stage_id" class="workspace-input workspace-select">
                  <option value="">Cortesia general del evento</option>
                  <option v-for="stage in stageTickets" :key="`issue-stage-${stage.id}`" :value="String(stage.id)">{{ stage.name }}</option>
                </select>
              </label>
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Notas</span>
                <textarea v-model="issueForm.notes" class="workspace-input workspace-input--textarea" rows="3" placeholder="Prensa, sponsor, artista, staff..."></textarea>
              </label>
            </div>
          </section>
        </template>

        <template v-else-if="modalMode === 'link'">
          <section class="courtesy-section">
            <div class="courtesy-section__head">
              <h3>{{ editingTicketId ? 'Editar plantilla de cortesia' : 'Crear link de cortesia' }}</h3>
              <p>Todo se crea desde aqui mismo. Si la ligas a una etapa, queda separada como cortesia propia para conservar reportes limpios.</p>
            </div>

            <div class="courtesy-field-grid">
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Etapa base opcional</span>
                <select v-model="templateForm.stage_id" class="workspace-input workspace-select" @change="applyTemplateStageDefaults">
                  <option value="">Cortesia general</option>
                  <option v-for="stage in stageTickets" :key="`template-stage-${stage.id}`" :value="String(stage.id)">{{ stage.name }}</option>
                </select>
              </label>
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Nombre interno</span>
                <input v-model.trim="templateForm.name" class="workspace-input" type="text" placeholder="Guest list prensa" required />
              </label>
              <label class="workspace-field">
                <span>Cantidad</span>
                <input v-model.number="templateForm.quantity" class="workspace-input" type="number" min="1" max="500" required />
              </label>
              <label class="workspace-field">
                <span>Forma de acceso</span>
                <select v-model="templateForm.delivery" class="workspace-input workspace-select">
                  <option value="private">Link privado</option>
                  <option value="checkout">Visible en checkout</option>
                </select>
              </label>
              <label v-if="templateForm.delivery === 'private'" class="workspace-field">
                <span>Codigo de cortesia</span>
                <input v-model.trim="templateForm.code" class="workspace-input" type="text" placeholder="Se autogenera si lo dejas vacio" />
              </label>
              <label class="workspace-field courtesy-field-grid__wide">
                <span>Notas</span>
                <textarea v-model="templateForm.description" class="workspace-input workspace-input--textarea" rows="3" placeholder="Uso interno, sponsor, artista, staff..."></textarea>
              </label>
            </div>
          </section>
        </template>

        <template v-else>
          <section class="courtesy-section">
            <div class="courtesy-section__head courtesy-section__head--spread">
              <div>
                <h3>Importar emisiones por CSV</h3>
                <p>Cada fila intenta emitir una cortesia directa. Puedes dejar la etapa vacia o usar una por defecto desde aqui.</p>
              </div>
              <button class="workspace-chip-button" type="button" @click="downloadCourtesyCsvDemo">
                <i class="fas fa-file-download"></i>
                <span>CSV demo</span>
              </button>
            </div>

            <label class="workspace-field courtesy-field-grid__wide">
              <span>Etapa por defecto opcional</span>
              <select v-model="bulkDefaultStageId" class="workspace-input workspace-select">
                <option value="">Cortesia general del evento</option>
                <option v-for="stage in stageTickets" :key="`bulk-stage-${stage.id}`" :value="String(stage.id)">{{ stage.name }}</option>
              </select>
            </label>

            <div
              :class="['courtesy-dropzone', { 'is-dragging': csvDragging, 'has-file': Boolean(importFile) }]"
              @dragenter.prevent="csvDragging = true"
              @dragover.prevent="csvDragging = true"
              @dragleave.prevent="csvDragging = false"
              @drop.prevent="onFileDrop"
            >
              <input ref="csvInput" class="courtesy-dropzone__input" type="file" accept=".csv" @change="onFileSelected" />
              <div class="courtesy-dropzone__copy">
                <strong>{{ importFile ? importFile.name : 'Arrastra tu CSV aqui' }}</strong>
                <p>{{ importFile ? `${csvPreview.rows.length} filas detectadas` : 'O selecciona un archivo desde tu computadora.' }}</p>
              </div>
              <div class="courtesy-dropzone__actions">
                <button class="workspace-button" type="button" @click.prevent="$refs.csvInput && $refs.csvInput.click()">
                  <i class="fas fa-upload"></i>
                  <span>{{ importFile ? 'Cambiar archivo' : 'Seleccionar archivo' }}</span>
                </button>
                <button v-if="importFile" class="workspace-button" type="button" @click.prevent="clearImportFile">
                  <i class="fas fa-times"></i>
                  <span>Quitar</span>
                </button>
              </div>
            </div>

            <div v-if="csvPreview.headers.length" class="courtesy-preview">
              <div class="courtesy-preview__meta">
                <span class="workspace-badge">Columnas: {{ csvPreview.headers.join(', ') }}</span>
                <span class="workspace-badge">Preview: {{ csvPreviewRows.length }} filas</span>
              </div>
              <div class="workspace-table-shell">
                <table class="workspace-table courtesy-preview__table">
                  <thead>
                    <tr>
                      <th v-for="header in csvPreview.headers" :key="`header-${header}`">{{ header }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in csvPreviewRows" :key="`row-${rowIndex}`">
                      <td v-for="header in csvPreview.headers" :key="`cell-${rowIndex}-${header}`">{{ row[header] || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </template>

        <div class="courtesy-form-actions">
          <button class="workspace-button workspace-button--ghost" type="button" @click="closeModal">
            <i class="fas fa-times"></i>
            <span>Cerrar</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="modalSaving">
            <i :class="modalSaving ? 'fas fa-spinner fa-spin' : 'fas fa-bolt'"></i>
            <span>{{ modalSaving ? 'Guardando...' : modalPrimaryAction }}</span>
          </button>
        </div>
      </form>
    </WorkspaceWizardModal>
  </section>
</template>

<script>
import JSZip from 'jszip'
import QRCode from 'qrcode'
import { apiService } from '../../common/api.services.js'
import WorkspaceWizardModal from '../../components/workspace/WorkspaceWizardModal.vue'

const DEFAULT_TEMPLATE_FORM = () => ({
  stage_id: '',
  name: '',
  quantity: 1,
  code: '',
  description: '',
  delivery: 'private',
})

const DEFAULT_ISSUE_FORM = () => ({
  first_name: '',
  last_name: '',
  email: '',
  stage_id: '',
  notes: '',
})

export default {
  name: 'CourtesyView',

  components: {
    WorkspaceWizardModal,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      modalSaving: false,
      savingTicketId: null,
      zipGenerating: false,
      issuedZipGenerating: false,
      issuedDownloadId: null,
      successMessage: '',
      errorMessage: '',
      tickets: [],
      viewMode: 'manual',
      modalOpen: false,
      modalScene: 'launcher',
      modalMode: 'issue',
      editingTicketId: null,
      templateForm: DEFAULT_TEMPLATE_FORM(),
      issueForm: DEFAULT_ISSUE_FORM(),
      bulkDefaultStageId: '',
      importFile: null,
      csvDragging: false,
      csvPreview: {
        headers: [],
        rows: [],
      },
      lastGeneratedLinks: [],
      lastIssuedBatch: [],
      lastIssuedBatchSource: '',
    }
  },

  computed: {
    stageTickets() {
      return [...this.tickets]
        .filter((ticket) => !ticket.is_courtesy)
        .sort((left, right) => new Date(right.sales_date || right.created_at || 0).getTime() - new Date(left.sales_date || left.created_at || 0).getTime())
    },

    courtesyTickets() {
      return [...this.tickets]
        .filter((ticket) => ticket.is_courtesy)
        .sort((left, right) => new Date(right.sales_date || right.created_at || 0).getTime() - new Date(left.sales_date || left.created_at || 0).getTime())
    },

    codedCourtesyCount() {
      return this.courtesyTickets.filter((ticket) => Number(ticket.code_mode) === 2).length
    },

    stageBoundCourtesyCount() {
      return this.courtesyTickets.filter((ticket) => ticket.courtesy_source_ticket_id).length
    },

    activeCourtesyCount() {
      return this.courtesyTickets.filter((ticket) => ticket.status).length
    },

    courtesyTabs() {
      return [
        {
          id: 'manual',
          label: 'Emitir 1 a 1',
          icon: 'fas fa-paper-plane',
          eyebrow: 'Directo',
          helper: 'Resuelve un invitado puntual con nombre, mail y etapa opcional.',
          meta: '1 persona por vez',
          kicker: 'Directas',
          title: 'Cortesias manuales',
          copy: 'Entrega una cortesia al instante para prensa, staff, artista o invitado especial sin armar una plantilla aparte.',
          actionLabel: 'Emitir cortesia',
          actionIcon: 'fas fa-bolt',
          modalMode: 'issue',
        },
        {
          id: 'codes',
          label: 'Links y codigos',
          icon: 'fas fa-link',
          eyebrow: 'Compartible',
          helper: 'Crea una plantilla con link privado, codigo o salida visible en checkout.',
          meta: `${this.codedCourtesyCount} con codigo`,
          kicker: 'Compartibles',
          title: 'Codigos y links de cortesia',
          copy: 'Arma accesos reutilizables para sponsors, aliados o listas privadas y compártelos luego sin rehacer la configuración.',
          actionLabel: 'Crear link o codigo',
          actionIcon: 'fas fa-key',
          modalMode: 'link',
        },
        {
          id: 'csv',
          label: 'Importar CSV',
          icon: 'fas fa-file-import',
          eyebrow: 'Masivo',
          helper: 'Sube un archivo y emite un lote completo con etapa opcional.',
          meta: 'Lote desde archivo',
          kicker: 'Masivo',
          title: 'Importaciones por CSV',
          copy: 'Ideal cuando ya tienes la base armada y quieres emitir muchas cortesías sin ir persona por persona.',
          actionLabel: 'Importar CSV',
          actionIcon: 'fas fa-upload',
          modalMode: 'bulk',
        },
      ]
    },

    activeCourtesyTab() {
      return this.courtesyTabs.find((tab) => tab.id === this.viewMode) || this.courtesyTabs[0]
    },

    issuedBatchKicker() {
      return this.lastIssuedBatchSource === 'csv' ? 'Ultima importacion' : 'Ultima emision'
    },

    issuedBatchTitle() {
      return this.lastIssuedBatchSource === 'csv' ? 'Resultados del CSV' : 'Resultados de la emision manual'
    },

    issuedBatchCopy() {
      return this.lastIssuedBatchSource === 'csv'
        ? 'Cuando termine el lote puedes bajar un ZIP con los tickets emitidos y, si hace falta, el CSV de control.'
        : 'Confirma a quien ya le emitiste la cortesia y descarga el resumen si lo necesita operaciones.'
    },

    manualIssuedEntry() {
      if (this.lastIssuedBatchSource !== 'manual') return null
      return this.lastIssuedBatch[0] || null
    },

    issuedBatchReadyEntries() {
      return this.lastIssuedBatch.filter((entry) => entry.status === 'ok' && entry.ticket_url)
    },

    modalTitle() {
      if (this.modalScene === 'launcher') return 'Nueva cortesia'
      if (this.modalMode === 'issue') return 'Emitir ahora'
      if (this.modalMode === 'bulk') return 'Importar CSV'
      return this.editingTicketId ? 'Editar plantilla de cortesia' : 'Crear link de cortesia'
    },

    modalCopy() {
      if (this.modalScene === 'launcher') return 'Elige la forma mas rapida para crear una cortesia sin salir de esta etapa.'
      if (this.modalMode === 'issue') return 'Emision directa para una persona con correo y etapa opcional.'
      if (this.modalMode === 'bulk') return 'CSV orientado a personas: nombre, apellido, mail y etapa opcional.'
      return 'Crea una plantilla privada o visible en checkout desde el mismo modal.'
    },

    modalPrimaryAction() {
      if (this.modalMode === 'issue') return 'Emitir cortesia'
      if (this.modalMode === 'bulk') return 'Procesar CSV'
      return this.editingTicketId ? 'Guardar plantilla' : 'Crear link'
    },

    csvPreviewRows() {
      return this.csvPreview.rows.slice(0, 5)
    },

    courtesyLauncherCards() {
      return [
        {
          mode: 'issue',
          kicker: 'Rapido',
          title: 'Emitir ahora',
          copy: 'Nombre, apellido, mail y etapa opcional. Ideal para prensa, sponsor, artista o staff.',
          action: 'Ir a emision directa',
          icon: 'fas fa-paper-plane',
        },
        {
          mode: 'link',
          kicker: 'Reusable',
          title: 'Crear link',
          copy: 'Arma una plantilla privada o visible en checkout, con etapa base opcional y link listo para compartir.',
          action: 'Crear plantilla',
          icon: 'fas fa-link',
        },
        {
          mode: 'bulk',
          kicker: 'Masivo',
          title: 'Importar CSV',
          copy: 'Procesa lotes con nombre, apellido, mail y etapa opcional desde un solo archivo.',
          action: 'Subir CSV',
          icon: 'fas fa-file-import',
        },
      ]
    },
  },

  created() {
    this.fetchTickets()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchTickets()
      },
    },
  },

  methods: {
    fetchTickets() {
      if (!this.event || !this.event.id) return

      this.loading = true
      apiService(`/api/host/events/${this.event.id}/tickets/`).then((data) => {
        this.tickets = Array.isArray(data) ? data : []
      }).finally(() => {
        this.loading = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    resetModalState() {
      this.editingTicketId = null
      this.modalScene = 'launcher'
      this.modalMode = 'issue'
      this.templateForm = DEFAULT_TEMPLATE_FORM()
      this.issueForm = DEFAULT_ISSUE_FORM()
      this.bulkDefaultStageId = ''
      this.importFile = null
      this.csvDragging = false
      this.csvPreview = { headers: [], rows: [] }
    },

    openComposer(mode = null) {
      this.resetMessages()
      this.resetModalState()
      this.modalOpen = true
      if (mode) {
        this.chooseModalMode(mode)
      }
    },

    closeModal() {
      this.modalOpen = false
      this.modalSaving = false
      this.resetModalState()
    },

    backToLauncher() {
      if (this.modalSaving) return
      this.editingTicketId = null
      this.modalScene = 'launcher'
    },

    chooseModalMode(mode) {
      this.modalMode = mode
      this.modalScene = 'form'
    },

    editCourtesy(ticket) {
      this.resetMessages()
      this.viewMode = 'codes'
      this.modalOpen = true
      this.modalScene = 'form'
      this.modalMode = 'link'
      this.editingTicketId = ticket.id
      this.templateForm = {
        stage_id: ticket.courtesy_source_ticket_id ? String(ticket.courtesy_source_ticket_id) : '',
        name: ticket.name || '',
        quantity: Number(ticket.initial_quantity) || 1,
        code: this.primaryCourtesyCode(ticket) || '',
        description: ticket.description || '',
        delivery: ticket.web_experience ? 'checkout' : 'private',
      }
    },

    applyTemplateStageDefaults() {
      const stage = this.stageTickets.find((ticket) => Number(ticket.id) === Number(this.templateForm.stage_id))
      if (!stage) return

      if (!String(this.templateForm.name || '').trim() || !this.editingTicketId) {
        this.templateForm.name = `${stage.name} · Cortesia`
      }

      if (!String(this.templateForm.description || '').trim()) {
        this.templateForm.description = stage.description || ''
      }
    },

    primaryCourtesyCode(ticket) {
      if (Array.isArray(ticket.access_codes_detail) && ticket.access_codes_detail.length) {
        return ticket.access_codes_detail[0].code
      }
      return ticket.code || ''
    },

    courtesyDeliveryLabel(ticket) {
      if (ticket.web_experience) return 'Visible en checkout'
      if (Number(ticket.code_mode) === 2) return 'Link + codigo'
      return 'Link privado'
    },

    courtesyLink(ticket) {
      const stageBase = `${window.location.origin}/event/${this.event.slug}?stage=${ticket.id}`
      const code = this.primaryCourtesyCode(ticket)
      if (code) {
        return `${stageBase}&co=${encodeURIComponent(code)}&wanted=true`
      }
      if (ticket.web_experience) {
        return stageBase
      }
      return `${stageBase}&wanted=true`
    },

    copyCourtesyLink(ticket) {
      const link = this.courtesyLink(ticket)
      if (!link) {
        this.errorMessage = 'Esta cortesia todavia no tiene link publico.'
        return
      }

      navigator.clipboard.writeText(link).then(() => {
        this.successMessage = `Link de ${ticket.name} copiado.`
      }).catch(() => {
        this.errorMessage = 'No se pudo copiar el link.'
      })
    },

    generateCourtesyCode(name = '') {
      const seed = slugify(name || 'courtesy').slice(0, 14) || 'COURTESY'
      return `CTY-${seed}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
    },

    buildTicketPayload({ name, quantity, description, code = '', visibleInCheckout = false, courtesySourceTicketId = '' }) {
      return {
        name,
        description,
        initial_quantity: Number(quantity) || 1,
        price: 0,
        status: true,
        web_experience: Boolean(visibleInCheckout),
        is_courtesy: true,
        is_physical: false,
        type: 3,
        category: 5,
        sales_date: null,
        expiration_date: null,
        require_ig: false,
        quantity: 1,
        max_quantity: 1,
        max_code_uses: null,
        code_mode: code ? 2 : 0,
        code,
        courtesy_source_ticket_id: courtesySourceTicketId || null,
      }
    },

    async submitCourtesy() {
      this.resetMessages()

      if (this.modalMode === 'issue') {
        await this.submitIssueCourtesy()
        return
      }

      if (this.modalMode === 'bulk') {
        await this.submitBulkCourtesyImport()
        return
      }

      await this.submitTemplateCourtesy()
    },

    async submitIssueCourtesy() {
      if (!String(this.issueForm.first_name || '').trim() || !String(this.issueForm.last_name || '').trim()) {
        this.errorMessage = 'Completa nombre y apellido.'
        return
      }

      if (!String(this.issueForm.email || '').trim()) {
        this.errorMessage = 'Completa el correo del invitado.'
        return
      }

      this.modalSaving = true
      try {
        const data = await apiService(`/api/host/events/${this.event.id}/courtesies/issue/`, 'POST', {
          first_name: this.issueForm.first_name,
          last_name: this.issueForm.last_name,
          email: this.issueForm.email,
          stage_id: this.issueForm.stage_id || '',
          notes: this.issueForm.notes,
        })

        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo emitir la cortesia.'
          return
        }

        this.lastIssuedBatch = [{
          holder_name: data.holder_name,
          email: data.customer_email,
          stage_name: data.source_stage_name || data.ticket_name,
          status: 'ok',
          detail: `Factura ${data.invoice_id}`,
          payment_id: data.payment_id,
          invoice_id: data.invoice_id,
          ticket_id: data.ticket_id,
          ticket_name: data.ticket_name,
          ticket_ready: data.ticket_ready,
          ticket_url: data.ticket_url,
        }]
        this.lastIssuedBatchSource = 'manual'
        this.viewMode = 'manual'
        this.lastGeneratedLinks = []
        this.successMessage = `Cortesia emitida para ${data.holder_name || data.customer_email}. Ya puedes descargar el ticket.`
        this.fetchTickets()
        this.closeModal()
      } finally {
        this.modalSaving = false
      }
    },

    async submitTemplateCourtesy() {
      const hasSelectedStage = Boolean(String(this.templateForm.stage_id || '').trim())
      if (!String(this.templateForm.name || '').trim() && !hasSelectedStage) {
        this.errorMessage = 'Escribe el nombre de la plantilla o elige una etapa base.'
        return
      }

      if (hasSelectedStage && !String(this.templateForm.name || '').trim()) {
        this.applyTemplateStageDefaults()
      }

      this.modalSaving = true
      const generatedCode = this.templateForm.delivery === 'private'
        ? String(this.templateForm.code || this.generateCourtesyCode(this.templateForm.name)).trim().toUpperCase()
        : ''
      const payload = this.buildTicketPayload({
        name: this.templateForm.name,
        quantity: this.templateForm.quantity,
        description: this.templateForm.description,
        code: generatedCode,
        visibleInCheckout: this.templateForm.delivery === 'checkout',
        courtesySourceTicketId: this.templateForm.stage_id,
      })

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`
      const method = this.editingTicketId ? 'PATCH' : 'POST'

      try {
        const data = await apiService(endpoint, method, payload)
        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo guardar la plantilla.'
          return
        }

        this.successMessage = this.editingTicketId ? 'Plantilla de cortesia actualizada.' : 'Plantilla de cortesia creada.'
        this.viewMode = 'codes'
        if (!data.web_experience) {
          this.lastGeneratedLinks = [this.ticketToLinkEntry(data)]
        } else {
          this.lastGeneratedLinks = []
        }
        this.fetchTickets()
        this.closeModal()
      } finally {
        this.modalSaving = false
      }
    },

    buildBulkIssuePayload(row, index) {
      const firstName = String(row.first_name || row.nombre || row.name || '').trim()
      const lastName = String(row.last_name || row.apellido || row.lastname || '').trim()
      const email = String(row.email || row.mail || '').trim().toLowerCase()
      const notes = String(row.notes || row.notas || '').trim()
      if (!email) {
        throw new Error(`La fila ${index + 1} no tiene email.`)
      }

      return {
        first_name: firstName,
        last_name: lastName,
        email,
        stage_id: this.resolveStageIdFromRow(row, index),
        notes,
      }
    },

    resolveStageIdFromRow(row, index) {
      const fallbackStageId = String(this.bulkDefaultStageId || '').trim()
      const rawStage = String(row.stage || row.etapa || row.stage_id || row.ticket_id || '').trim()
      if (!rawStage) {
        return fallbackStageId
      }

      const normalizedLookup = slugify(rawStage)
      const matchedStage = this.stageTickets.find((ticket) => {
        const keys = [String(ticket.id), slugify(ticket.name)]
        return keys.includes(rawStage) || keys.includes(normalizedLookup)
      })
      if (matchedStage) {
        return String(matchedStage.id)
      }

      throw new Error(`No encontramos la etapa "${rawStage}" en la fila ${index + 1}.`)
    },

    async submitBulkCourtesyImport() {
      if (!this.importFile) {
        this.errorMessage = 'Sube un CSV antes de importar.'
        return
      }

      if (this.csvPreview.rows.length > 100) {
        this.errorMessage = 'Para esta version el maximo recomendado es 100 filas.'
        return
      }

      this.modalSaving = true
      const results = []
      const tasks = this.csvPreview.rows.map((row, index) => async () => {
        try {
          const payload = this.buildBulkIssuePayload(row, index)
          const data = await apiService(`/api/host/events/${this.event.id}/courtesies/issue/`, 'POST', payload)
          if (!data || data.err) {
            throw new Error((data && data.err) || 'No se pudo emitir la cortesia.')
          }

          results.push({
            holder_name: data.holder_name,
            email: data.customer_email,
            stage_name: data.source_stage_name || data.ticket_name,
            status: 'ok',
            detail: `Factura ${data.invoice_id}`,
            payment_id: data.payment_id,
            invoice_id: data.invoice_id,
            ticket_id: data.ticket_id,
            ticket_name: data.ticket_name,
            ticket_ready: data.ticket_ready,
            ticket_url: data.ticket_url,
          })
        } catch (error) {
          results.push({
            holder_name: String(row.first_name || row.nombre || row.name || '').trim(),
            email: String(row.email || row.mail || '').trim().toLowerCase(),
            stage_name: String(row.stage || row.etapa || this.bulkDefaultStageId || '').trim(),
            status: 'error',
            detail: error && error.message ? error.message : 'No se pudo emitir la cortesia.',
          })
        }
      })

      try {
        await runWithConcurrency(tasks, 4)
        const issuedCount = results.filter((entry) => entry.status === 'ok').length
        const failedCount = results.length - issuedCount
        this.lastIssuedBatch = results
        this.lastIssuedBatchSource = 'csv'
        this.viewMode = 'csv'
        this.lastGeneratedLinks = []
        this.successMessage = `Procesamos ${results.length} filas: ${issuedCount} emitidas y ${failedCount} con error.`
        this.fetchTickets()
        this.closeModal()
      } finally {
        this.modalSaving = false
      }
    },

    ticketToLinkEntry(ticket) {
      return {
        ticketId: ticket.id,
        name: ticket.name,
        code: this.primaryCourtesyCode(ticket),
        link: this.courtesyLink(ticket),
      }
    },

    async downloadQrZip() {
      if (!this.lastGeneratedLinks.length) {
        this.errorMessage = 'Todavia no hay un lote con links para descargar.'
        return
      }

      this.zipGenerating = true
      try {
        const zip = new JSZip()
        await runWithConcurrency(this.lastGeneratedLinks.map((entry) => async () => {
          const dataUrl = await QRCode.toDataURL(entry.link, { width: 720, margin: 1 })
          const base64 = dataUrl.split(',')[1]
          zip.file(`${slugify(entry.name || entry.code || 'courtesy') || 'courtesy'}-${entry.code || entry.ticketId}.png`, base64, { base64: true })
        }), 4)

        const blob = await zip.generateAsync({ type: 'blob' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `courtesies-${this.event.slug || 'event'}.zip`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (_error) {
        this.errorMessage = 'No se pudo generar el ZIP de QRs.'
      } finally {
        this.zipGenerating = false
      }
    },

    downloadLastBatchCsv() {
      if (!this.lastGeneratedLinks.length) return

      const lines = [
        'name,code,link',
        ...this.lastGeneratedLinks.map((entry) => `${csvCell(entry.name)},${csvCell(entry.code)},${csvCell(entry.link)}`),
      ]
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `courtesies-${this.event.slug || 'event'}-links.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    },

    async downloadIssuedTicket(entry) {
      if (!entry || !entry.ticket_url) {
        this.errorMessage = 'Esta emision todavia no tiene ticket descargable.'
        return
      }

      this.issuedDownloadId = entry.payment_id || entry.invoice_id || entry.ticket_id || 'issued-ticket'
      try {
        const response = await fetch(resolveBrowserUrl(entry.ticket_url), { credentials: 'include' })
        if (!response.ok) {
          throw new Error('No se pudo descargar el ticket.')
        }

        const blob = await response.blob()
        triggerBrowserDownload(blob, buildIssuedTicketFilename(entry))
      } catch (_error) {
        this.errorMessage = 'No se pudo descargar el ticket de la cortesia.'
      } finally {
        this.issuedDownloadId = null
      }
    },

    async downloadIssuedTicketsZip() {
      if (!this.issuedBatchReadyEntries.length) {
        this.errorMessage = 'Todavia no hay tickets listos para comprimir.'
        return
      }

      this.issuedZipGenerating = true
      try {
        const zip = new JSZip()
        await runWithConcurrency(this.issuedBatchReadyEntries.map((entry, index) => async () => {
          const response = await fetch(resolveBrowserUrl(entry.ticket_url), { credentials: 'include' })
          if (!response.ok) {
            throw new Error(`No se pudo descargar el ticket ${index + 1}.`)
          }

          const blob = await response.blob()
          zip.file(buildIssuedTicketFilename(entry, index + 1), blob)
        }), 3)

        const blob = await zip.generateAsync({ type: 'blob' })
        triggerBrowserDownload(blob, `courtesies-${this.event.slug || 'event'}-tickets.zip`)
      } catch (_error) {
        this.errorMessage = 'No se pudo generar el ZIP de tickets emitidos.'
      } finally {
        this.issuedZipGenerating = false
      }
    },

    downloadIssuedResultsCsv() {
      if (!this.lastIssuedBatch.length) return

      const lines = [
        'holder_name,email,stage_name,status,detail,invoice_id,payment_id,ticket_url',
        ...this.lastIssuedBatch.map((entry) => `${csvCell(entry.holder_name)},${csvCell(entry.email)},${csvCell(entry.stage_name)},${csvCell(entry.status)},${csvCell(entry.detail)},${csvCell(entry.invoice_id)},${csvCell(entry.payment_id)},${csvCell(entry.ticket_url)}`),
      ]
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `courtesies-${this.event.slug || 'event'}-issued.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    },

    downloadCourtesyCsvDemo() {
      const csv = [
        'first_name,last_name,email,stage,notes',
        'Sofia,Ramirez,sofia@email.com,VIP,Invitada prensa',
        'Martin,Lopez,martin@email.com,,Sponsor general',
        'Ana,Torres,ana@email.com,General,Staff operativo',
      ].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'fomo-courtesies-demo.csv'
      link.click()
      window.URL.revokeObjectURL(url)
    },

    async applyImportFile(file) {
      this.resetMessages()
      this.importFile = file
      try {
        const text = await file.text()
        this.csvPreview = parseCsvText(text)
      } catch (_error) {
        this.errorMessage = 'No pudimos leer el CSV.'
        this.importFile = null
        this.csvPreview = { headers: [], rows: [] }
      }
    },

    onFileSelected(event) {
      const nextFile = event && event.target && event.target.files && event.target.files[0]
      if (!nextFile) return
      this.applyImportFile(nextFile)
    },

    onFileDrop(event) {
      this.csvDragging = false
      const nextFile = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      if (!nextFile) return
      this.applyImportFile(nextFile)
    },

    clearImportFile() {
      this.importFile = null
      this.csvPreview = { headers: [], rows: [] }
      if (this.$refs.csvInput) {
        this.$refs.csvInput.value = ''
      }
    },

    toggleCourtesyStatus(ticket) {
      this.resetMessages()
      this.savingTicketId = ticket.id
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'PATCH', {
        status: !ticket.status,
      }).then((data) => {
        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo actualizar la cortesia.'
          return
        }

        this.successMessage = ticket.status ? 'Cortesia pausada.' : 'Cortesia reactivada.'
        this.fetchTickets()
      }).finally(() => {
        this.savingTicketId = null
      })
    },
  },
}

function parseCsvText(text) {
  const lines = []
  let row = []
  let current = ''
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const next = text[index + 1]

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === ',' && !inQuotes) {
      row.push(current)
      current = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1
      row.push(current)
      if (row.some((value) => String(value || '').trim() !== '')) {
        lines.push(row)
      }
      row = []
      current = ''
      continue
    }

    current += char
  }

  if (current || row.length) {
    row.push(current)
    if (row.some((value) => String(value || '').trim() !== '')) {
      lines.push(row)
    }
  }

  if (!lines.length) {
    return { headers: [], rows: [] }
  }

  const headers = lines[0].map((value) => String(value || '').trim())
  const rows = lines.slice(1).map((sourceRow) => {
    const record = {}
    headers.forEach((header, headerIndex) => {
      record[header] = sourceRow[headerIndex] || ''
    })
    return record
  })

  return { headers, rows }
}

function slugify(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase()
}

function csvCell(value) {
  const text = String(value || '')
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

function resolveBrowserUrl(value) {
  return new URL(String(value || ''), window.location.origin).href
}

function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(url)
}

function buildIssuedTicketFilename(entry, fallbackIndex = '') {
  const holder = slugify(entry && (entry.holder_name || entry.email || entry.ticket_name)) || `COURTESY-${fallbackIndex || 'TICKET'}`
  const suffix = entry && (entry.invoice_id || entry.payment_id || entry.ticket_id || fallbackIndex || Date.now())
  return `${holder}-${suffix}.pdf`
}

async function runWithConcurrency(tasks, concurrency = 4) {
  const executing = []

  for (const task of tasks) {
    const promise = Promise.resolve().then(task)
    executing.push(promise)

    const remove = () => {
      const index = executing.indexOf(promise)
      if (index >= 0) executing.splice(index, 1)
    }

    promise.then(remove).catch(remove)

    if (executing.length >= concurrency) {
      await Promise.race(executing)
    }
  }

  await Promise.all(executing)
}
</script>

<style scoped>
.courtesy-hero-actions,
.courtesy-batch-preview,
.courtesy-confirmation-grid,
.courtesy-confirmation-card,
.courtesy-launcher-grid,
.courtesy-modal,
.courtesy-modal-topbar,
.courtesy-mode-grid,
.courtesy-mode-card,
.courtesy-mode-card__copy,
.courtesy-form-actions,
.courtesy-tab-brief__actions,
.courtesy-tab-brief,
.courtesy-field-grid,
.courtesy-section,
.courtesy-dropzone,
.courtesy-dropzone__copy,
.courtesy-dropzone__actions,
.courtesy-preview,
.courtesy-preview__meta {
  display: grid;
  gap: 16px;
}

.courtesy-modal .workspace-button {
  border-radius: 12px;
  box-shadow: none;
}

.courtesy-batch-preview,
.courtesy-modal-topbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.courtesy-launcher-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.courtesy-mode-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.courtesy-tabs-panel {
  gap: 18px;
}

.courtesy-issued-panel {
  gap: 18px;
}

.courtesy-tab-brief {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}

.courtesy-tab-brief__actions {
  justify-items: end;
  align-content: center;
}

.courtesy-tab-brief__badge {
  justify-self: end;
}

.courtesy-mode-card {
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.025);
  color: inherit;
  text-align: left;
  transition: border-color 180ms ease, background 180ms ease, transform 180ms ease, box-shadow 180ms ease;
}

.courtesy-mode-card:hover {
  transform: translateY(-2px);
  border-color: rgba(196, 206, 228, 0.2);
  background: rgba(255, 255, 255, 0.045);
}

.courtesy-mode-card.is-active {
  border-color: rgba(163, 230, 217, 0.28);
  background: linear-gradient(180deg, rgba(163, 230, 217, 0.12), rgba(255, 255, 255, 0.04));
  box-shadow: 0 18px 30px rgba(3, 8, 14, 0.2);
}

.courtesy-mode-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-main);
}

.courtesy-mode-card__copy {
  gap: 6px;
}

.courtesy-mode-card__copy small,
.courtesy-mode-card__copy p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.5;
}

.courtesy-mode-card__copy strong {
  color: var(--text-main);
  font-size: 1rem;
}

.courtesy-mode-card__meta {
  grid-column: 2;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(208, 219, 238, 0.72);
  font-weight: 700;
}

.courtesy-launcher-card {
  display: grid;
  gap: 12px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  color: inherit;
  text-align: left;
}

.courtesy-launcher-card:hover {
  border-color: rgba(196, 206, 228, 0.26);
  transform: translateY(-2px);
}

.courtesy-launcher-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-main);
}

.courtesy-launcher-card small,
.courtesy-launcher-card p,
.courtesy-section__head p,
.courtesy-dropzone__copy p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.55;
}

.courtesy-launcher-card strong,
.courtesy-section__head h3 {
  margin: 0;
  color: var(--text-main);
}

.courtesy-launcher-card__action {
  color: var(--text-main);
  font-weight: 700;
}

.courtesy-modal-topbar {
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 1;
  padding-bottom: 10px;
  background: linear-gradient(180deg, rgba(13, 18, 29, 0.98), rgba(13, 18, 29, 0));
}

.courtesy-modal {
  align-content: start;
  min-height: 100%;
  padding-bottom: 8px;
}

.courtesy-confirmation-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.courtesy-confirmation-card {
  gap: 4px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.courtesy-confirmation-card small,
.courtesy-confirmation-card p {
  margin: 0;
  color: var(--text-soft);
}

.courtesy-confirmation-card strong {
  display: block;
  margin: 6px 0 4px;
  color: var(--text-main);
}

.courtesy-section,
.courtesy-dropzone {
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.courtesy-section__head {
  display: grid;
  gap: 4px;
}

.courtesy-section__head--spread {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
}

.courtesy-field-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.courtesy-field-grid__wide {
  grid-column: 1 / -1;
}

.courtesy-dropzone.is-dragging,
.courtesy-dropzone.has-file {
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.055);
}

.courtesy-dropzone__input {
  display: none;
}

.courtesy-dropzone__actions,
.courtesy-hero-actions {
  grid-auto-flow: column;
  justify-content: start;
}

.courtesy-form-actions {
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, max-content));
  justify-content: end;
  align-items: center;
  margin-top: 8px;
  padding: 18px 0 8px;
  border-top: 1px solid rgba(196, 206, 228, 0.08);
}

.courtesy-form-actions .workspace-button {
  min-width: 168px;
}

.workspace-badge--danger {
  border-color: rgba(255, 122, 122, 0.28);
  background: rgba(255, 122, 122, 0.12);
  color: #ffd5d5;
}

@media (max-width: 1100px) {
  .courtesy-launcher-grid,
  .courtesy-confirmation-grid,
  .courtesy-mode-grid,
  .courtesy-tab-brief,
  .courtesy-field-grid,
  .courtesy-section__head--spread {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .courtesy-dropzone__actions,
  .courtesy-hero-actions {
    grid-auto-flow: row;
  }

  .courtesy-tab-brief__actions {
    justify-items: stretch;
  }

  .courtesy-tab-brief__badge {
    justify-self: start;
  }

  .courtesy-form-actions {
    grid-template-columns: 1fr;
    justify-content: stretch;
  }

  .courtesy-form-actions .workspace-button,
  .courtesy-hero-actions .workspace-button,
  .courtesy-tab-brief__actions .workspace-button,
  .courtesy-launcher-card {
    width: 100%;
  }

  .courtesy-modal {
    padding-bottom: 24px;
  }
}
</style>
